"use client";

import { useState } from "react";
import { type OrderStatus, orderStatusTranslation } from "@/interfaces";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CheckCircle2,
  Clock,
  Loader2,
  CheckCheck,
  XCircle,
  AlertTriangle,
  MousePointerClick,
  PackageCheck,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface OrderStatusUpdateProps {
  currentStatus: OrderStatus;
  onStatusUpdate: (newStatus: OrderStatus) => void;
}

export function OrderStatusUpdate({
  currentStatus,
  onStatusUpdate,
}: OrderStatusUpdateProps) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState<OrderStatus | null>(
    null
  );
  const [showHelp, setShowHelp] = useState(true);

  // Função para abrir o diálogo de confirmação
  const handleStatusClick = (status: OrderStatus) => {
    // Se o status atual for CANCELED, não permite alteração
    if (currentStatus === "CANCELED") return;

    // Se for o mesmo status, não faz nada
    if (status === currentStatus) return;

    setStatusToUpdate(status);
    setIsConfirmOpen(true);
    setShowHelp(false); // Esconde a dica após o primeiro clique
  };

  // Função para confirmar a atualização
  const handleConfirm = () => {
    if (statusToUpdate) {
      onStatusUpdate(statusToUpdate);
    }
    setIsConfirmOpen(false);
  };

  // Função para cancelar a atualização
  const handleCancel = () => {
    setStatusToUpdate(null);
    setIsConfirmOpen(false);
  };

  // Configuração dos status com ícones e cores
  const statusConfig = {
    PENDING: {
      icon: <Clock className="h-4 w-4 sm:h-5 sm:w-5" />,
      color: "bg-yellow-50 border-yellow-200 text-yellow-700",
      activeColor: "bg-yellow-100 border-yellow-300",
      hoverColor: "hover:bg-yellow-100 hover:border-yellow-300 hover:scale-105",
      confirmColor: "bg-yellow-600 hover:bg-yellow-700",
      confirmIcon: <Clock className="h-5 w-5 mr-2" />,
      confirmTitle: "Alterar para Pendente",
      confirmDescription:
        "Tem certeza que deseja alterar o status do pedido para Pendente?",
    },
    SCHEDULED: {
      icon: <Loader2 className="h-4 w-4 sm:h-5 sm:w-5" />,
      color: "bg-blue-50 border-blue-200 text-blue-700",
      activeColor: "bg-blue-100 border-blue-300",
      hoverColor: "hover:bg-blue-100 hover:border-blue-300 hover:scale-105",
      confirmColor: "bg-blue-600 hover:bg-blue-700",
      confirmIcon: <Loader2 className="h-5 w-5 mr-2" />,
      confirmTitle: "Agendar Pedido",
      confirmDescription:
        "Tem certeza que deseja alterar o status do pedido para Agendado?",
    },
    IN_PREPARATION: {
      icon: <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />,
      color: "bg-orange-50 border-orange-200 text-orange-700",
      activeColor: "bg-orange-100 border-orange-300",
      hoverColor: "hover:bg-orange-100 hover:border-orange-300 hover:scale-105",
      confirmColor: "bg-orange-600 hover:bg-orange-700",
      confirmIcon: <Loader2 className="h-5 w-5 mr-2" />,
      confirmTitle: "Iniciar Preparação",
      confirmDescription:
        "Tem certeza que deseja alterar o status do pedido para Em Preparação?",
    },
    PREPARED: {
      icon: <PackageCheck className="h-4 w-4 sm:h-5 sm:w-5" />,
      color: "bg-teal-50 border-teal-200 text-teal-700",
      activeColor: "bg-teal-100 border-teal-300",
      hoverColor: "hover:bg-teal-100 hover:border-teal-300 hover:scale-105",
      confirmColor: "bg-teal-600 hover:bg-teal-700",
      confirmIcon: <PackageCheck className="h-5 w-5 mr-2" />,
      confirmTitle: "Marcar como Pronto",
      confirmDescription:
        "Tem certeza que deseja alterar o status do pedido para Pronto?",
    },
    DONE: {
      icon: <CheckCheck className="h-4 w-4 sm:h-5 sm:w-5" />,
      color: "bg-green-50 border-green-200 text-green-700",
      activeColor: "bg-green-100 border-green-300",
      hoverColor: "hover:bg-green-100 hover:border-green-300 hover:scale-105",
      confirmColor: "bg-green-600 hover:bg-green-700",
      confirmIcon: <CheckCircle2 className="h-5 w-5 mr-2" />,
      confirmTitle: "Concluir Pedido",
      confirmDescription:
        "Tem certeza que deseja marcar este pedido como Concluído?",
    },
    CANCELED: {
      icon: <XCircle className="h-4 w-4 sm:h-5 sm:w-5" />,
      color: "bg-red-50 border-red-200 text-red-700",
      activeColor: "bg-red-100 border-red-300",
      hoverColor: "hover:bg-red-100 hover:border-red-300 hover:scale-105",
      confirmColor: "bg-red-600 hover:bg-red-700",
      confirmIcon: <AlertTriangle className="h-5 w-5 mr-2" />,
      confirmTitle: "Cancelar Pedido",
      confirmDescription:
        "Tem certeza que deseja cancelar este pedido? Esta ação não pode ser desfeita facilmente.",
    },
  };

  return (
    <>
      <div className="flex flex-col space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h3 className="text-sm font-medium">
              Status atual:{" "}
              <span className="font-bold">
                {orderStatusTranslation[currentStatus]}
              </span>
            </h3>

            {currentStatus === "CANCELED" && (
              <p className="text-xs text-red-600 mt-1 flex items-center">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Pedidos cancelados não podem ter seu status alterado
              </p>
            )}
          </div>

          {/* Dica de ajuda */}
          {showHelp && currentStatus !== "CANCELED" && (
            <div className="flex items-center text-primary text-xs sm:text-sm animate-pulse">
              <MousePointerClick className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              <span>Clique em um status para alterar</span>
            </div>
          )}
        </div>

        <div className="relative py-4">
          {/* Linha de progresso */}
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 -translate-y-1/2 z-0"></div>

          {/* Status steps */}
          <div className="relative z-10 flex justify-between">
            {(Object.keys(statusConfig) as OrderStatus[]).map((status) => {
              const config = statusConfig[status];
              const isActive = currentStatus === status;
              const isCanceled = currentStatus === "CANCELED";
              const isClickable = !isActive && !isCanceled;

              return (
                <TooltipProvider key={status}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex flex-col items-center">
                        <button
                          onClick={() => handleStatusClick(status)}
                          disabled={isActive || isCanceled}
                          className={`
                            relative flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full border-2 
                            transition-all duration-200 mb-1 sm:mb-2
                            ${
                              isActive
                                ? `${config.activeColor} ring-2 ring-offset-1 ring-primary/30`
                                : `${config.color}`
                            }
                            ${
                              isClickable
                                ? `${config.hoverColor} cursor-pointer`
                                : "cursor-default"
                            }
                            ${isClickable ? "shadow-sm hover:shadow-md" : ""}
                            ${
                              isCanceled && !isActive
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }
                          `}
                        >
                          {config.icon}

                          {/* Badge para status clicáveis */}
                          {isClickable && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-primary text-white rounded-full flex items-center justify-center text-[8px] sm:text-[10px] font-bold animate-bounce">
                              <MousePointerClick className="h-2 w-2 sm:h-3 sm:w-3" />
                            </div>
                          )}
                        </button>
                        <span
                          className={`text-[10px] sm:text-xs font-medium text-center ${
                            isActive ? "text-primary" : "text-gray-600"
                          }`}
                        >
                          {orderStatusTranslation[status]}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {isActive ? (
                        <p className="text-xs">Status atual</p>
                      ) : (
                        <p className="text-xs">
                          Clique para alterar para{" "}
                          {orderStatusTranslation[status]}
                        </p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
        </div>

        {/* Instrução adicional */}
        {currentStatus === "CANCELED" ? (
          <p className="text-[10px] sm:text-xs text-red-600 text-center font-medium">
            Este pedido foi cancelado e não pode ter seu status alterado
          </p>
        ) : (
          <p className="text-[10px] sm:text-xs text-muted-foreground text-center">
            Selecione um dos ícones acima para alterar o status do pedido
          </p>
        )}
      </div>

      {/* Diálogo de confirmação */}
      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {statusToUpdate && statusConfig[statusToUpdate].confirmIcon}
              {statusToUpdate && statusConfig[statusToUpdate].confirmTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusToUpdate &&
                statusConfig[statusToUpdate].confirmDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={
                statusToUpdate ? statusConfig[statusToUpdate].confirmColor : ""
              }
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
